import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiActions, rootUiSelectors, rootUiTypes } from 'app-components/flux/root-ui';
import { getLogger } from 'app-components/utils/core';
import { Map as ImmutableMap } from 'immutable';

import { useAppDispatch, useAppSelector } from 'hooks/redux';

import type { RootModalList, UrlDialogProps } from './types';

const log = getLogger('components/RootModal/useRootModalUrlDialogs');
const ALERT_DIALOG_TYPE = 'DIALOG_ALERT';

const isUrlDialogObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toUrlDialogProps = (value: unknown): UrlDialogProps | null => {
  if (!isUrlDialogObject(value) || typeof value.dialogType !== 'string') {
    return null;
  }

  return {
    ...value,
    createdAt: typeof value.createdAt === 'number' ? value.createdAt : undefined,
    dialogType: value.dialogType,
    id: typeof value.id === 'string' ? value.id : undefined,
  };
};

// Prefer the provided id, then createdAt, so URL-backed dialogs stay stable across renders and avoid duplicate root
// UI entries.
const getUrlDialogId = (props: UrlDialogProps, index: number): string =>
  props.id ??
  ((props.createdAt && `url-dialog-${props.dialogType}-${props.createdAt}`) ||
    `url-dialog-${props.dialogType}-${index}`);

const parseUrlDialogs = (searchProps: string | null): unknown[] => {
  if (!searchProps) return [];
  const parsed: unknown = JSON.parse(decodeURIComponent(searchProps));
  return Array.isArray(parsed) ? parsed : [];
};

const findUrlDialogs = (dialogs: unknown[], dialogId: string): unknown[] =>
  dialogs.filter((value, index) => {
    const props = toUrlDialogProps(value);
    return props !== null && getUrlDialogId(props, index) === dialogId;
  });

const readUrlDialogs = (searchProps: string | null): unknown[] => {
  try {
    return parseUrlDialogs(searchProps);
  } catch {
    return [];
  }
};

// URL entries removed from the current history entry, as exact JSON. Removals before the router renders the new URL
// all start from the same params, so each rewrite drops every one of them, or a later one would put an earlier one
// back. The set belongs to one history entry (location key): once the URL moves on, including Back to an entry that
// still lists a removed dialog, it no longer applies.
let pendingUrlDialogRemovals: { locationKey: string; entries: Set<string> } | undefined;

// Removes a root dialog record from wherever it came from: Redux, or the dialogs URL param, whose records
// useRootModalUrlDialogs builds without putting them in Redux. The callback keeps its identity across URL changes.
export const useRemoveRootModalRecord = (): ((dialogId: string) => void) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationKey = useLocation().key;
  const urlRef = useRef({ locationKey, searchParams, setSearchParams });
  useEffect(() => {
    urlRef.current = { locationKey, searchParams, setSearchParams };
  });

  return useCallback(
    (dialogId: string) => {
      dispatch(rootUiActions.removeDialog(dialogId));
      const url = urlRef.current;
      const entries = findUrlDialogs(readUrlDialogs(url.searchParams.get('dialogs')), dialogId);
      if (entries.length === 0) return;

      if (pendingUrlDialogRemovals?.locationKey !== url.locationKey) {
        pendingUrlDialogRemovals = { locationKey: url.locationKey, entries: new Set() };
      }
      const removedEntries = pendingUrlDialogRemovals.entries;
      entries.forEach((entry) => removedEntries.add(JSON.stringify(entry)));
      url.setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          const dialogs = readUrlDialogs(prevParams.get('dialogs')).filter(
            (value) => !removedEntries.has(JSON.stringify(value)),
          );
          if (dialogs.length > 0) {
            newParams.set('dialogs', encodeURIComponent(JSON.stringify(dialogs)));
          } else {
            newParams.delete('dialogs');
          }
          return newParams;
        },
        { replace: true },
      );
    },
    [dispatch],
  );
};

export const orderRootModalDialogs = (modalList: RootModalList): RootModalList =>
  modalList.sort((first, second) => {
    const alertOrder = Number(first.type === ALERT_DIALOG_TYPE) - Number(second.type === ALERT_DIALOG_TYPE);
    if (alertOrder !== 0) {
      return alertOrder;
    }

    return (first.createdAt ?? 0) - (second.createdAt ?? 0);
  });

export const useRootModalUrlDialogs = (): RootModalList => {
  const modalsSelector = useAppSelector(rootUiSelectors.getActiveModals) as RootModalList;
  const urlDialogFF = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'urlDialog'));
  const [searchParams, setSearchParams] = useSearchParams();

  const searchProps = searchParams.get('dialogs');
  // URL dialogs are encoded so refresh/deep-link flows can recreate root UI
  // entries alongside dialogs already present in Redux.
  const urlDialogs = useMemo<unknown[]>(() => {
    try {
      return parseUrlDialogs(searchProps);
    } catch (e) {
      log.error('Failed to parse search props for dialogs:', e);
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          newParams.delete('dialogs');
          return newParams;
        },
        { replace: true },
      );
      return [];
    }
  }, [searchProps, setSearchParams]);

  return useMemo(() => {
    let modalList = modalsSelector;
    if (urlDialogs && urlDialogFF) {
      const validDialogs: UrlDialogProps[] = [];
      let hasInvalidDialogs = false;

      urlDialogs.forEach((props, index) => {
        const urlDialogProps = toUrlDialogProps(props);

        if (!urlDialogProps) {
          log.warn('Skipping invalid dialog props:', props);
          hasInvalidDialogs = true;
          return;
        }

        validDialogs.push(urlDialogProps);

        const propsCopy: Record<string, unknown> = { ...urlDialogProps };
        Reflect.deleteProperty(propsCopy, 'dialogType');

        modalList = modalList.push(
          rootUiTypes.mkRootUiData({
            type: urlDialogProps.dialogType,
            props: ImmutableMap(propsCopy ?? {}),
            createdAt: urlDialogProps.createdAt,
            id: getUrlDialogId(urlDialogProps, index),
          }),
        );
      });

      if (hasInvalidDialogs) {
        setSearchParams(
          (prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            if (validDialogs.length > 0) {
              newParams.set('dialogs', encodeURIComponent(JSON.stringify(validDialogs)));
            } else {
              newParams.delete('dialogs');
            }
            return newParams;
          },
          { replace: true },
        );
      }
    }
    return orderRootModalDialogs(modalList);
  }, [modalsSelector, urlDialogs, urlDialogFF, setSearchParams]);
};
